import React, { useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';
import { useToast } from './Toast';

export function NewsComposerModal({ isOpen, onClose }) {
  const toast = useToast();
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [step, setStep] = useState(1);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds([]);
    setSendToAll(false);
    setStep(1);
    setContent('');
    setLoading(true);
    api.getNewsRecipients()
      .then(setRecipients)
      .catch(error => toast(error.message, 'error'))
      .finally(() => setLoading(false));
  }, [isOpen, toast]);

  if (!isOpen) return null;

  const toggleRecipient = (id) => {
    setSendToAll(false);
    setSelectedIds(current => current.includes(id)
      ? current.filter(item => item !== id)
      : [...current, id]);
  };

  const canContinue = sendToAll ? recipients.length > 0 : selectedIds.length > 0;

  const publish = async () => {
    if (!content.trim() || publishing) return;
    setPublishing(true);
    try {
      const result = await api.publishNews({
        content: content.trim(),
        all: sendToAll,
        user_ids: sendToAll ? [] : selectedIds,
      });
      toast(`News envoyée à ${result.recipient_count} destinataire${result.recipient_count > 1 ? 's' : ''}`, 'success');
      onClose();
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="modal-content news-composer" role="dialog" aria-modal="true" aria-labelledby="news-composer-title">
        <div className="modal-header">
          <div>
            <div className="news-step-label">Étape {step} sur 2</div>
            <h2 id="news-composer-title">📰 Nouvelle News</h2>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">&times;</button>
        </div>

        {step === 1 ? (
          <>
            <p className="news-composer-help">Choisissez les comptes qui verront la popup. Les comptes administrateurs sont automatiquement exclus.</p>
            {loading ? (
              <div className="news-recipient-empty loading-pulse">Chargement des destinataires…</div>
            ) : recipients.length === 0 ? (
              <div className="news-recipient-empty">Aucun destinataire disponible.</div>
            ) : (
              <div className="news-recipient-list">
                <label className={`news-recipient-option news-recipient-all ${sendToAll ? 'selected' : ''}`}>
                  <input type="checkbox" checked={sendToAll} onChange={event => {
                    setSendToAll(event.target.checked);
                    if (event.target.checked) setSelectedIds([]);
                  }} />
                  <span className="news-recipient-avatar all">Tous</span>
                  <span><strong>Tous les utilisateurs</strong><small>{recipients.length} compte{recipients.length > 1 ? 's' : ''}, hors administrateurs</small></span>
                </label>
                <div className="news-recipient-divider"><span>ou sélection individuelle</span></div>
                {recipients.map(recipient => (
                  <label key={recipient.id} className={`news-recipient-option ${selectedIds.includes(recipient.id) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={selectedIds.includes(recipient.id)} onChange={() => toggleRecipient(recipient.id)} />
                    <span className="news-recipient-avatar" style={{ background: recipient.color || '#3b7ddd' }}>
                      {recipient.username.slice(0, 2).toUpperCase()}
                    </span>
                    <strong>{recipient.username}</strong>
                  </label>
                ))}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
              <button className="btn btn-primary" disabled={!canContinue} onClick={() => setStep(2)}>Continuer</button>
            </div>
          </>
        ) : (
          <>
            <p className="news-composer-help">
              Destinataires : <strong>{sendToAll ? `Tous (${recipients.length})` : selectedIds.map(id => recipients.find(r => r.id === id)?.username).filter(Boolean).join(', ')}</strong>
            </p>
            <div className="form-group">
              <label htmlFor="news-content">Message de la popup</label>
              <textarea
                id="news-content"
                className="form-textarea news-message-input"
                value={content}
                onChange={event => setContent(event.target.value.slice(0, 5000))}
                placeholder="Saisissez votre information…"
                rows={8}
                autoFocus
              />
              <div className="news-character-count">{content.length} / 5 000</div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setStep(1)} disabled={publishing}>Retour</button>
              <button className="btn news-publish-button" onClick={publish} disabled={!content.trim() || publishing}>
                {publishing ? 'Publication…' : 'Publier la News'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function NewsPopup({ news, onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!news) return undefined;
    closeButtonRef.current?.focus();
    const preventEscape = event => {
      if (event.key === 'Escape') event.preventDefault();
    };
    document.addEventListener('keydown', preventEscape);
    return () => document.removeEventListener('keydown', preventEscape);
  }, [news]);

  if (!news) return null;

  return (
    <div className="news-popup-overlay" role="presentation">
      <div className="news-popup" role="alertdialog" aria-modal="true" aria-label="Breaking News" aria-describedby="news-popup-message">
        <div className="news-breaking-banner" aria-hidden="true">
          <span className="news-breaking-main">BREAKING</span>
          <span className="news-breaking-title">NEWS</span>
          <span className="news-breaking-ticker">› NEWS › NEWS › NEWS</span>
        </div>
        <div className="news-popup-body">
          <span className="news-info-icon" aria-hidden="true">i</span>
          <p id="news-popup-message">{news.content}</p>
        </div>
        <div className="news-popup-footer">
          <button ref={closeButtonRef} className="news-close-button" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
